import { useState, type ReactNode } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';

import { dialogClose, dialogOpen } from '@/modules/MuiDialog';
import { formatLocaleDate, formatLocaleNumber } from '@/utils/formatter';
import type { ChangePlanPreviewDto } from '@/types/subscription';

import { ACCENT, ACCENT_HOVER } from '../_shared';

interface ChangePlanDialogBodyProps {
    preview: ChangePlanPreviewDto;
    planName: string;
    onConfirm: () => Promise<void> | void;
    onCancel: () => void;
}

const GuideRow = ({ children }: { children: ReactNode }) => (
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled', mt: '7px', flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {children}
        </Typography>
    </Stack>
);

const Formula = ({ children }: { children: ReactNode }) => (
    <Box
        component="code"
        sx={{
            display: 'inline-block',
            px: 0.65,
            py: 0.1,
            mx: 0.25,
            bgcolor: 'rgba(0,0,0,0.06)',
            borderRadius: 0.5,
            fontFamily: '"SF Mono", "JetBrains Mono", ui-monospace, monospace',
            fontSize: 11,
            color: 'text.primary',
        }}
    >
        {children}
    </Box>
);

const ChangePlanDialogBody = ({ preview, planName, onConfirm, onCancel }: ChangePlanDialogBodyProps) => {
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm();
        } finally {
            setSubmitting(false);
        }
    };

    const cycleLabel = preview.billingCycle === 'yearly' ? '년' : '월';

    const title =
        preview.type === 'upgrade'
            ? `${planName}로 업그레이드`
            : preview.type === 'free_downgrade'
            ? 'Free 플랜으로 변경'
            : `${planName}로 변경`;

    const confirmText = preview.type === 'upgrade' ? '업그레이드' : '변경 예약';

    return (
        <>
            <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
            <DialogContent dividers sx={{ minWidth: { sm: 380 } }}>
                <Stack spacing={2}>
                    {/* 결제 정보 헤더 */}
                    {preview.type === 'upgrade' && (
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                지금 결제할 금액
                            </Typography>
                            <Stack direction="row" alignItems="baseline" spacing={1}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: ACCENT,
                                        fontWeight: 900,
                                        lineHeight: 1.1,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    ₩{formatLocaleNumber(preview.prorationAmount)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    즉시 적용
                                </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                다음 결제일부터 ₩{formatLocaleNumber(preview.nextBillingAmount)}/{cycleLabel}
                            </Typography>
                        </Stack>
                    )}

                    {preview.type === 'free_downgrade' && (
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                결제할 금액
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                없음
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {formatLocaleDate(preview.effectiveAt)}에 자동 해지
                            </Typography>
                        </Stack>
                    )}

                    {preview.type === 'downgrade' && (
                        <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary">
                                지금 결제할 금액
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                없음
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {formatLocaleDate(preview.effectiveAt)}부터 ₩
                                {formatLocaleNumber(preview.nextBillingAmount)}/{cycleLabel}
                            </Typography>
                        </Stack>
                    )}

                    {/* 가이드 박스 */}
                    <Box
                        sx={{
                            p: 1.75,
                            bgcolor: 'rgba(0,0,0,0.03)',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', fontWeight: 700, mb: 0.75, color: 'text.primary' }}
                        >
                            {preview.type === 'upgrade' ? '💡 차액 계산 방식' : '💡 변경 정책'}
                        </Typography>
                        <Stack spacing={0.5}>
                            {preview.type === 'upgrade' && (
                                <>
                                    <GuideRow>
                                        이용 중인 결제 주기의 <b>잔여 일수에 대한 두 플랜의 차액</b>만 결제돼요.
                                    </GuideRow>
                                    <GuideRow>
                                        계산식
                                        <Formula>(새 플랜가 − 기존 플랜가) × 잔여일 ÷ 총일</Formula>
                                    </GuideRow>
                                    <GuideRow>
                                        이용 기간과 다음 결제일은 <b>그대로 유지</b>돼요.
                                    </GuideRow>
                                    <GuideRow>
                                        다음 결제일부터는 ₩{formatLocaleNumber(preview.nextBillingAmount)}/
                                        {cycleLabel}로 자동 결제돼요.
                                    </GuideRow>
                                </>
                            )}
                            {preview.type === 'free_downgrade' && (
                                <>
                                    <GuideRow>
                                        이번 결제 주기 동안은 <b>현재 플랜을 그대로</b> 사용할 수 있어요.
                                    </GuideRow>
                                    <GuideRow>
                                        다음 결제일에 <b>자동으로 해지</b>되고, 환불은 발생하지 않아요.
                                    </GuideRow>
                                    <GuideRow>예약은 언제든 "변경 예약 취소"로 되돌릴 수 있어요.</GuideRow>
                                </>
                            )}
                            {preview.type === 'downgrade' && (
                                <>
                                    <GuideRow>
                                        이번 결제 주기 동안은 <b>현재 플랜을 그대로</b> 사용할 수 있어요.
                                    </GuideRow>
                                    <GuideRow>
                                        다음 결제일에 새 플랜으로 <b>자동 전환</b>되고 새 가격으로 결제돼요.
                                    </GuideRow>
                                    <GuideRow>예약은 언제든 "변경 예약 취소"로 되돌릴 수 있어요.</GuideRow>
                                </>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onCancel}
                    disabled={submitting}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 2, textTransform: 'none', cursor: 'pointer' }}
                >
                    취소
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={submitting}
                    variant="contained"
                    disableElevation
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{
                        bgcolor: ACCENT,
                        color: '#fff',
                        borderRadius: 2,
                        textTransform: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(251,109,72,0.35)',
                        transition: 'transform 120ms ease, background-color 120ms ease, box-shadow 200ms ease',
                        '&:hover': {
                            bgcolor: ACCENT_HOVER,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 8px 24px rgba(251,109,72,0.45)',
                        },
                        '&.Mui-disabled': { bgcolor: ACCENT, opacity: 0.6, color: '#fff' },
                    }}
                >
                    {submitting ? '처리 중…' : confirmText}
                </Button>
            </DialogActions>
        </>
    );
};

/**
 * 플랜 변경 다이얼로그 열기.
 * - preview 결과 기반으로 업그레이드/다운/Free 다운 각 케이스의 가이드 표시.
 */
export function openChangePlanDialog(args: {
    preview: ChangePlanPreviewDto;
    planName: string;
    onConfirm: () => Promise<void> | void;
}): void {
    dialogOpen({
        maxWidth: 'xs' as any,
        fullWidth: true,
        innerElement: (
            <ChangePlanDialogBody
                preview={args.preview}
                planName={args.planName}
                onCancel={() => dialogClose()}
                onConfirm={async () => {
                    await args.onConfirm();
                    dialogClose();
                }}
            />
        ),
    });
}
